/* eslint-disable @typescript-eslint/no-explicit-any */

function ensureDir(oc: any, dir: string) {
  try {
    oc.FS.stat(dir)
  } catch {
    oc.FS.mkdir(dir)
  }
}

function toValue(x: any) {
  return x && typeof x === 'object' && 'value' in x ? (x as any).value : x
}

function pickStepModelType(oc: any) {
  const t = oc.STEPControl_StepModelType
  // AsIs 模式将整个 shape 作为单一实体导出（不拆分 compound 中的子实体）
  // ManifoldSolidBrep 模式会将每个子 solid 拆分为独立 product（导致 STEP 查看器显示多个层）
  return t?.STEPControl_AsIs ?? t?.STEPControl_ManifoldSolidBrep ?? 0
}

function makeProgress(oc: any) {
  try {
    if (oc.Message_ProgressRange_1) return new oc.Message_ProgressRange_1()
    if (oc.Message_ProgressRange) return new oc.Message_ProgressRange()
  } catch {
    // ignore
  }
  return undefined
}

function stepTransfer(writer: any, shape: any, mode: any, progress: any) {
  try {
    return writer.Transfer(shape, mode, true, progress)
  } catch {
    try {
      return writer.Transfer(shape, mode, false, progress)
    } catch {
      return writer.Transfer(shape, mode, true)
    }
  }
}

/** Match keys that are exactly `prefix` or `prefix_N` (not `prefix2d_N` etc.) */
function matchExact(key: string, prefix: string): boolean {
  if (key === prefix) return true
  if (key.startsWith(prefix + '_')) return true
  return false
}

function norm3(x: number, y: number, z: number) {
  return Math.sqrt(x * x + y * y + z * z)
}

function normalize3(x: number, y: number, z: number) {
  const n = norm3(x, y, z)
  if (n < 1e-12) throw new Error('Zero-length vector')
  return { x: x / n, y: y / n, z: z / n }
}

function cross3(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number
) {
  return {
    x: ay * bz - az * by,
    y: az * bx - ax * bz,
    z: ax * by - ay * bx,
  }
}

function probeNoArg(oc: any, prefix: string) {
  const names = Object.keys(oc).filter(k => matchExact(k, prefix)).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const obj = new Ctor()
      if (obj) return obj
    } catch {
      // wrong overload, try next
    }
  }
  throw new Error(`No ${prefix} no-arg constructor found`)
}

function probeXYZ(oc: any, prefix: string, x: number, y: number, z: number) {
  const names = Object.keys(oc).filter(k => matchExact(k, prefix)).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const obj = new Ctor(x, y, z)
      if (obj) return obj
    } catch {
      // wrong overload, try next
    }
  }
  // fallback: try bare name
  if (typeof oc[prefix] === 'function') {
    return new oc[prefix](x, y, z)
  }
  throw new Error(`No ${prefix} overload accepts (x, y, z)`)
}

export function pnt(oc: any, x: number, y: number, z: number) {
  return probeXYZ(oc, 'gp_Pnt', x, y, z)
}

export function vec(oc: any, x: number, y: number, z: number) {
  return probeXYZ(oc, 'gp_Vec', x, y, z)
}

export function dir(oc: any, x: number, y: number, z: number) {
  return probeXYZ(oc, 'gp_Dir', x, y, z)
}

export function makeBox(oc: any, dx: number, dy: number, dz: number) {
  const names = Object.keys(oc).filter(k => k.startsWith('BRepPrimAPI_MakeBox_')).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const inst = new Ctor(dx, dy, dz)
      const s = inst?.Shape?.()
      if (s) return s
    } catch {
      // ignore
    }
  }
  throw new Error('MakeBox failed')
}

export function makeCylinderZ(oc: any, radius: number, height: number) {
  const names = Object.keys(oc).filter(k => k.startsWith('BRepPrimAPI_MakeCylinder_')).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const inst = new Ctor(radius, height)
      const s = inst?.Shape?.()
      if (s) return s
    } catch {
      // ignore
    }
  }
  throw new Error('MakeCylinder failed')
}

export function makeConeZ(
  oc: any,
  radiusBottom: number,
  radiusTop: number,
  height: number
) {
  const names = Object.keys(oc).filter(k => k.startsWith('BRepPrimAPI_MakeCone_')).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const inst = new Ctor(radiusBottom, radiusTop, height)
      const s = inst?.Shape?.()
      if (s) return s
    } catch {
      // ignore
    }
  }
  throw new Error('MakeCone failed')
}

export function translate(oc: any, shape: any, dx: number, dy: number, dz: number) {
  const trsf = probeNoArg(oc, 'gp_Trsf')
  const v = vec(oc, dx, dy, dz)

  // Emscripten prototype methods aren't enumerable via Object.keys.
  // Try all known suffixed overloads directly.
  const candidates = [
    'SetTranslation_2', 'SetTranslation_1', 'SetTranslation_3',
    'SetTranslation_4', 'SetTranslation',
  ]
  let translated = false
  for (const m of candidates) {
    if (typeof trsf[m] !== 'function') continue
    try {
      trsf[m](v)
      translated = true
      break
    } catch {
      // wrong overload, try next
    }
  }
  if (!translated) {
    throw new Error('gp_Trsf: no SetTranslation overload accepted gp_Vec')
  }

  // Probe BRepBuilderAPI_Transform constructors
  const names = Object.keys(oc).filter(k => k.startsWith('BRepBuilderAPI_Transform_')).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const tx = new Ctor(shape, trsf, true)
      const s = tx?.Shape?.()
      if (s) return s
    } catch {
      // try next
    }
  }
  throw new Error('BRepBuilderAPI_Transform failed')
}

export function transform(oc: any, shape: any, trsf: any) {
  const names = Object.keys(oc).filter(k => matchExact(k, 'BRepBuilderAPI_Transform')).sort()
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const tx = new Ctor(shape, trsf, true)
      const s = tx?.Shape?.()
      if (s) return s
    } catch {
      // try next
    }
  }
  throw new Error('BRepBuilderAPI_Transform failed')
}

function callBuild(op: any) {
  const candidates = ['Build_1', 'Build_2', 'Build']
  for (const m of candidates) {
    if (typeof op[m] === 'function') {
      try { op[m](); return } catch { /* next */ }
    }
  }
  // Build might not be needed for some overloads
}

function callShape(op: any): any {
  const candidates = ['Shape', 'Shape_1', 'Shape_2']
  for (const m of candidates) {
    if (typeof op[m] === 'function') {
      try {
        const s = op[m]()
        if (s) return s
      } catch { /* next */ }
    }
  }
  return null
}

export function fuse(oc: any, a: any, b: any) {
  const names = Object.keys(oc).filter(k => matchExact(k, 'BRepAlgoAPI_Fuse')).sort()
  const progress = makeProgress(oc)
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    if (progress) {
      try {
        const op = new Ctor(a, b, progress)
        callBuild(op)
        const s = callShape(op)
        if (s) return s
      } catch { /* next */ }
    }
    try {
      const op = new Ctor(a, b)
      callBuild(op)
      const s = callShape(op)
      if (s) return s
    } catch { /* next */ }
  }
  throw new Error('BRepAlgoAPI_Fuse failed')
}

/**
 * 将多个 shape 合并为一个 TopoDS_Compound
 * 用于在 boolean cut 之前把同一 layer 的所有切割体合并
 */
export function makeCompound(oc: any, shapes: any[]): any {
  if (shapes.length === 0) throw new Error('makeCompound: empty shapes')
  if (shapes.length === 1) return shapes[0]

  // 构建 TopoDS_Compound
  const compoundNames = Object.keys(oc).filter(k => matchExact(k, 'TopoDS_Compound')).sort()
  let compound: any = null
  for (const n of compoundNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try { compound = new Ctor(); if (compound) break } catch { /* next */ }
  }
  if (!compound) throw new Error('TopoDS_Compound construction failed')

  // 构建 BRep_Builder
  const builderNames = Object.keys(oc).filter(k => matchExact(k, 'BRep_Builder')).sort()
  let builder: any = null
  for (const n of builderNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try { builder = new Ctor(); if (builder) break } catch { /* next */ }
  }
  if (!builder) throw new Error('BRep_Builder construction failed')

  // MakeCompound
  const makeCandidates = ['MakeCompound', 'MakeCompound_1', 'MakeCompound_2']
  let made = false
  for (const m of makeCandidates) {
    if (typeof builder[m] !== 'function') continue
    try { builder[m](compound); made = true; break } catch { /* next */ }
  }
  if (!made) throw new Error('BRep_Builder.MakeCompound failed')

  // Add each shape
  const addCandidates = ['Add', 'Add_1', 'Add_2', 'Add_3']
  for (const shape of shapes) {
    let added = false
    for (const m of addCandidates) {
      if (typeof builder[m] !== 'function') continue
      try { builder[m](compound, shape); added = true; break } catch { /* next */ }
    }
    if (!added) throw new Error('BRep_Builder.Add failed')
  }

  return compound
}

export function cut(oc: any, a: any, b: any) {
  const names = Object.keys(oc).filter(k => matchExact(k, 'BRepAlgoAPI_Cut')).sort()
  const progress = makeProgress(oc)
  for (const n of names) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    // Try (S1, S2, ProgressRange)
    if (progress) {
      try {
        const op = new Ctor(a, b, progress)
        callBuild(op)
        const s = callShape(op)
        if (s) return s
      } catch { /* next */ }
    }
    // Try (S1, S2)
    try {
      const op = new Ctor(a, b)
      callBuild(op)
      const s = callShape(op)
      if (s) return s
    } catch { /* next */ }
  }
  throw new Error('BRepAlgoAPI_Cut failed')
}

/**
 * 建立一个沿 X 方向的 box，再通过刚体变换放到 start->end 上
 * width 沿局部 Y，height 沿局部 Z
 */
export function makeLineBox(
  oc: any,
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  width: number,
  height: number
) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const dz = end.z - start.z
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)

  if (len <= 1e-9) {
    throw new Error('LineShape has zero length')
  }

  const ex = { x: dx / len, y: dy / len, z: dz / len }
  
  // Calculate local Y axis (width direction) - must be horizontal
  // Y = Z_world x ex
  const wx = -ex.y;
  const wy = ex.x;
  const nW = Math.sqrt(wx * wx + wy * wy);
  let widthDir = { x: 0, y: 1, z: 0 };
  if (nW > 1e-9) {
    widthDir = { x: wx / nW, y: wy / nW, z: 0 };
  }
  
  // Calculate local Z axis (height direction)
  // Z_local = ex x Y_local
  let heightDir = cross3(ex.x, ex.y, ex.z, widthDir.x, widthDir.y, widthDir.z);
  const nH = norm3(heightDir.x, heightDir.y, heightDir.z);
  if (nH > 1e-9) {
    heightDir = { x: heightDir.x / nH, y: heightDir.y / nH, z: heightDir.z / nH };
  } else {
    heightDir = { x: 0, y: 0, z: 1 };
  }

  const hw = width / 2;
  const hh = height / 2;

  // Create face at 'start'
  const p1 = {
    x: start.x - widthDir.x * hw - heightDir.x * hh,
    y: start.y - widthDir.y * hw - heightDir.y * hh,
    z: start.z - widthDir.z * hw - heightDir.z * hh
  };
  const p2 = {
    x: start.x + widthDir.x * hw - heightDir.x * hh,
    y: start.y + widthDir.y * hw - heightDir.y * hh,
    z: start.z + widthDir.z * hw - heightDir.z * hh
  };
  const p3 = {
    x: start.x + widthDir.x * hw + heightDir.x * hh,
    y: start.y + widthDir.y * hw + heightDir.y * hh,
    z: start.z + widthDir.z * hw + heightDir.z * hh
  };
  const p4 = {
    x: start.x - widthDir.x * hw + heightDir.x * hh,
    y: start.y - widthDir.y * hw + heightDir.y * hh,
    z: start.z - widthDir.z * hw + heightDir.z * hh
  };

  const directionVec = { x: dx, y: dy, z: dz };
  
  return makePolygonPrism(oc, [p1, p2, p3, p4], directionVec);
}

/**
 * points 先建平面 polygon，再按 direction 拉伸
 */
export function makePolygonPrism(
  oc: any,
  points: Array<{ x: number; y: number; z: number }>,
  directionVec: { x: number; y: number; z: number }
) {
  if (!points || points.length < 3) {
    throw new Error('PolygonShape needs at least 3 points')
  }

  const wireMaker = probeNoArg(oc, 'BRepBuilderAPI_MakePolygon')

  // Add points — method might be Add_1, Add_2, etc.
  for (const pt of points) {
    const p = pnt(oc, pt.x, pt.y, pt.z)
    const addCandidates = ['Add_1', 'Add_2', 'Add_3', 'Add']
    let added = false
    for (const m of addCandidates) {
      if (typeof wireMaker[m] !== 'function') continue
      try { wireMaker[m](p); added = true; break } catch { /* next */ }
    }
    if (!added) throw new Error('BRepBuilderAPI_MakePolygon: no Add overload accepted gp_Pnt')
  }

  // Close
  const closeCandidates = ['Close', 'Close_1', 'Close_2']
  for (const m of closeCandidates) {
    if (typeof wireMaker[m] === 'function') {
      try { wireMaker[m](); break } catch { /* next */ }
    }
  }

  // Wire
  const wireCandidates = ['Wire', 'Wire_1', 'Wire_2']
  let wire: any = null
  for (const m of wireCandidates) {
    if (typeof wireMaker[m] !== 'function') continue
    try { wire = wireMaker[m](); if (wire) break } catch { /* next */ }
  }
  if (!wire) throw new Error('BRepBuilderAPI_MakePolygon: Wire() failed')

  // MakeFace — probe constructors that accept a wire
  // The wire-only overload varies across builds; try (wire, true), (wire, false), and (wire)
  const faceNames = Object.keys(oc).filter(k => matchExact(k, 'BRepBuilderAPI_MakeFace')).sort()
  let face: any = null
  const faceTries = [
    (Ctor: any) => new Ctor(wire, true),
    (Ctor: any) => new Ctor(wire, false),
    (Ctor: any) => new Ctor(wire),
  ]
  outer: for (const n of faceNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    for (const tryFn of faceTries) {
      try {
        const fm = tryFn(Ctor)
        const f = fm?.Face?.() ?? fm?.Face_1?.() ?? fm?.Face_2?.()
        if (f) { face = f; break outer }
      } catch { /* next */ }
    }
  }
  if (!face) throw new Error('BRepBuilderAPI_MakeFace failed')

  // MakePrism — probe constructors
  const v = vec(oc, directionVec.x, directionVec.y, directionVec.z)
  const prismNames = Object.keys(oc).filter(k => matchExact(k, 'BRepPrimAPI_MakePrism')).sort()
  for (const n of prismNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const prism = new Ctor(face, v, true, true)
      const s = prism?.Shape?.()
      if (s) return s
    } catch { /* next */ }
    try {
      const prism = new Ctor(face, v)
      const s = prism?.Shape?.()
      if (s) return s
    } catch { /* next */ }
  }
  throw new Error('BRepPrimAPI_MakePrism failed')
}

export function makePolygonFace(
  oc: any,
  points: Array<{ x: number; y: number; z: number }>
) {
  if (!points || points.length < 3) {
    throw new Error('makePolygonFace requires at least 3 points')
  }

  const wireMaker = probeNoArg(oc, 'BRepBuilderAPI_MakePolygon')

  for (const pt of points) {
    const p = pnt(oc, pt.x, pt.y, pt.z)
    const addCandidates = ['Add_1', 'Add_2', 'Add_3', 'Add']
    let added = false
    for (const m of addCandidates) {
      if (typeof wireMaker[m] !== 'function') continue
      try { wireMaker[m](p); added = true; break } catch { /* next */ }
    }
    if (!added) throw new Error('makePolygonFace: no Add overload accepted gp_Pnt')
  }

  const closeCandidates = ['Close', 'Close_1', 'Close_2']
  for (const m of closeCandidates) {
    if (typeof wireMaker[m] === 'function') {
      try { wireMaker[m](); break } catch { /* next */ }
    }
  }

  const wireCandidates = ['Wire', 'Wire_1', 'Wire_2']
  let wire: any = null
  for (const m of wireCandidates) {
    if (typeof wireMaker[m] !== 'function') continue
    try { wire = wireMaker[m](); if (wire) break } catch { /* next */ }
  }
  if (!wire) throw new Error('makePolygonFace: Wire() failed')

  const faceNames = Object.keys(oc).filter(k => matchExact(k, 'BRepBuilderAPI_MakeFace')).sort()
  let face: any = null
  const faceTries = [
    (Ctor: any) => new Ctor(wire, true),
    (Ctor: any) => new Ctor(wire, false),
    (Ctor: any) => new Ctor(wire),
  ]
  outer: for (const n of faceNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    for (const tryFn of faceTries) {
      try {
        const fm = tryFn(Ctor)
        const f = fm?.Face?.() ?? fm?.Face_1?.() ?? fm?.Face_2?.()
        if (f) { face = f; break outer }
      } catch { /* next */ }
    }
  }
  if (!face) throw new Error('makePolygonFace: MakeFace failed')

  return face
}

export function makeArcPipeRect(
  oc: any,
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  center: { x: number; y: number; z: number },
  width: number,
  height: number,
  tangent?: { x: number; y: number; z: number }
) {
  function dot3(ax: number, ay: number, az: number, bx: number, by: number, bz: number) {
    return ax * bx + ay * by + az * bz;
  }

  const v1 = { x: start.x - center.x, y: start.y - center.y, z: start.z - center.z };
  const v2 = { x: end.x - center.x, y: end.y - center.y, z: end.z - center.z };
  
  let axDir = cross3(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
  let nAx = norm3(axDir.x, axDir.y, axDir.z);

  // If 180 degrees or points are collinear, try to use tangent to define the plane
  if (nAx < 1e-6 && tangent) {
    axDir = cross3(v1.x, v1.y, v1.z, tangent.x, tangent.y, tangent.z);
    nAx = norm3(axDir.x, axDir.y, axDir.z);
  }

  // Final fallback to Z axis
  if (nAx < 1e-6) {
    axDir = { x: 0, y: 0, z: 1 };
    nAx = 1;
  }
  axDir = { x: axDir.x / nAx, y: axDir.y / nAx, z: axDir.z / nAx };

  const c12 = cross3(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
  const sinTheta = dot3(c12.x, c12.y, c12.z, axDir.x, axDir.y, axDir.z) / (norm3(v1.x, v1.y, v1.z) * norm3(v2.x, v2.y, v2.z));
  const cosTheta = dot3(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z) / (norm3(v1.x, v1.y, v1.z) * norm3(v2.x, v2.y, v2.z));
  
  let revolAngle = Math.atan2(Math.abs(sinTheta), cosTheta);
  if (revolAngle < 1e-6) revolAngle = 2 * Math.PI;

  // Compute tangent at start (must be orthogonal to v1 and in the plane)
  let t_start = cross3(axDir.x, axDir.y, axDir.z, v1.x, v1.y, v1.z);
  const nT = norm3(t_start.x, t_start.y, t_start.z);
  t_start = { x: t_start.x / nT, y: t_start.y / nT, z: t_start.z / nT };

  // Calculate local profile axes matching makeLineBox behavior
  let widthDir = cross3(0, 0, 1, t_start.x, t_start.y, t_start.z);
  const nW = norm3(widthDir.x, widthDir.y, widthDir.z);
  if (nW < 1e-6) {
    widthDir = { x: 1, y: 0, z: 0 };
  } else {
    widthDir = { x: widthDir.x / nW, y: widthDir.y / nW, z: widthDir.z / nW };
  }

  let heightDir = cross3(t_start.x, t_start.y, t_start.z, widthDir.x, widthDir.y, widthDir.z);
  const nH = norm3(heightDir.x, heightDir.y, heightDir.z);
  heightDir = { x: heightDir.x / nH, y: heightDir.y / nH, z: heightDir.z / nH };

  // 1) 构造旋转轴 gp_Ax1
  const centerPnt = pnt(oc, center.x, center.y, center.z);
  const ax1Dir = dir(oc, axDir.x, axDir.y, axDir.z);

  const ax1Names = Object.keys(oc).filter(k => matchExact(k, 'gp_Ax1')).sort();
  let ax1: any = null;
  for (const n of ax1Names) {
    const Ctor = oc[n];
    if (typeof Ctor !== 'function') continue;
    try {
      ax1 = new Ctor(centerPnt, ax1Dir);
      if (ax1) { console.log('[CURVE] ax1 built with', n); break; }
    } catch { /* next */ }
  }
  if (!ax1) throw new Error('gp_Ax1 construction failed');

  // 2) 在起点建立矩形截面
  const hw = width / 2;
  const hh = height / 2;

  const p1 = {
    x: start.x - widthDir.x * hw - heightDir.x * hh,
    y: start.y - widthDir.y * hw - heightDir.y * hh,
    z: start.z - widthDir.z * hw - heightDir.z * hh,
  };
  const p2 = {
    x: start.x + widthDir.x * hw - heightDir.x * hh,
    y: start.y + widthDir.y * hw - heightDir.y * hh,
    z: start.z + widthDir.z * hw - heightDir.z * hh,
  };
  const p3 = {
    x: start.x + widthDir.x * hw + heightDir.x * hh,
    y: start.y + widthDir.y * hw + heightDir.y * hh,
    z: start.z + widthDir.z * hw + heightDir.z * hh,
  };
  const p4 = {
    x: start.x - widthDir.x * hw + heightDir.x * hh,
    y: start.y - widthDir.y * hw + heightDir.y * hh,
    z: start.z - widthDir.z * hw + heightDir.z * hh,
  };

  const profileFace = makePolygonFace(oc, [p1, p2, p3, p4]);

  // 3) BRepPrimAPI_MakeRevol
  const revolNames = Object.keys(oc)
    .filter(k => k.startsWith('BRepPrimAPI_MakeRevol_'))
    .sort();

  let result: any = null;

  for (const n of revolNames) {
    const Ctor = oc[n];
    if (typeof Ctor !== 'function') continue;
    try {
      const inst = new Ctor(profileFace, ax1, revolAngle, true);
      const s = inst?.Shape?.();
      if (s) {
        result = s;
        console.log('[CURVE] revol built with', n, 'angle:', revolAngle);
        break;
      }
    } catch { /* next */ }
    try {
      const inst = new Ctor(profileFace, ax1, revolAngle);
      const s = inst?.Shape?.();
      if (s) {
        result = s;
        console.log('[CURVE] revol built with', n, 'angle:', revolAngle);
        break;
      }
    } catch { /* next */ }
  }

  if (!result) {
    throw new Error('BRepPrimAPI_MakeRevol failed');
  }

  return result;
}

/**
 * 使用 ShapeUpgrade_UnifySameDomain 合并相邻共面面
 * 将 boolean cut 后产生的多个子 solid 合并为单一实体
 */
export function unifyShape(oc: any, shape: any): any {
  // 方案 1: ShapeUpgrade_UnifySameDomain
  const unifyNames = Object.keys(oc).filter(k => matchExact(k, 'ShapeUpgrade_UnifySameDomain')).sort()
  for (const n of unifyNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const unifier = new Ctor(shape, true, true, true)
      if (typeof unifier.Build === 'function') unifier.Build()
      else if (typeof unifier.Build_1 === 'function') unifier.Build_1()
      const result = unifier.Shape?.() ?? unifier.Shape_1?.()
      if (result) {
        console.log('[STEP] UnifySameDomain OK')
        return result
      }
    } catch { /* next */ }
    try {
      const unifier = new Ctor(shape, true, true)
      if (typeof unifier.Build === 'function') unifier.Build()
      else if (typeof unifier.Build_1 === 'function') unifier.Build_1()
      const result = unifier.Shape?.() ?? unifier.Shape_1?.()
      if (result) {
        console.log('[STEP] UnifySameDomain OK (2-arg)')
        return result
      }
    } catch { /* next */ }
    try {
      const unifier = new Ctor(shape)
      if (typeof unifier.Build === 'function') unifier.Build()
      else if (typeof unifier.Build_1 === 'function') unifier.Build_1()
      const result = unifier.Shape?.() ?? unifier.Shape_1?.()
      if (result) {
        console.log('[STEP] UnifySameDomain OK (1-arg)')
        return result
      }
    } catch { /* next */ }
  }

  // 方案 2: BRepBuilderAPI_Sewing
  const sewNames = Object.keys(oc).filter(k => matchExact(k, 'BRepBuilderAPI_Sewing')).sort()
  for (const n of sewNames) {
    const Ctor = oc[n]
    if (typeof Ctor !== 'function') continue
    try {
      const sewer = new Ctor(1e-6)
      const addCandidates = ['Add', 'Add_1', 'Add_2']
      for (const m of addCandidates) {
        if (typeof sewer[m] === 'function') {
          try { sewer[m](shape); break } catch { /* next */ }
        }
      }
      if (typeof sewer.Perform === 'function') sewer.Perform()
      else if (typeof sewer.Perform_1 === 'function') sewer.Perform_1()
      const result = sewer.SewedShape?.() ?? sewer.SewedShape_1?.()
      if (result) {
        console.log('[STEP] Sewing OK')
        return result
      }
    } catch { /* next */ }
  }

  console.warn('[STEP] unifyShape: no method worked, returning original')
  return shape
}

export async function exportSTEP(oc: any, shape: any, filename = 'fluid3d.step'): Promise<Blob> {
  if (!oc?.STEPControl_Writer_1) {
    throw new Error('STEPControl_Writer_1 not found')
  }

  ensureDir(oc, '/tmp')
  const out = `/tmp/${filename}`

  const writer = new oc.STEPControl_Writer_1()
  const mode = pickStepModelType(oc)
  const progress = makeProgress(oc)

  // 直接使用原始 shape（UnifySameDomain 会错误合并内部圆孔面）
  const tr = stepTransfer(writer, shape, mode, progress)
  console.log('[STEP] Transfer returned:', tr, 'value:', toValue(tr))

  const wr = writer.Write(out)
  console.log('[STEP] Write returned:', wr, 'value:', toValue(wr))

  const st = oc.FS.stat(out)
  console.log('[STEP] FS.stat ok, size:', st.size)

  const data = oc.FS.readFile(out) as Uint8Array
const buffer = data.buffer.slice(
  data.byteOffset,
  data.byteOffset + data.byteLength
) as ArrayBuffer

return new Blob([buffer], { type: 'application/step' })
}